import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { VerSportsLogo } from '@versports/ui'
import { auth } from '../api/auth'
import { useAuth } from '../auth/AuthContext'
import { PinForm } from '../components/PinForm'
import { hasAdminAreaAccess } from '../auth/postLoginRoute'
import { openVrApp, vrBridgeError } from '../auth/vrBridge'

/**
 * `/` — para un JUGADOR logueado esto ya no es una home: es el PUENTE a la
 * superficie de juego. Canjea la cookie por un bearer
 * (`POST /auth/exchange-token`) y salta al lobby del vr-app
 * (`/vr/?token=…`), que es donde viven el "Comenzar", el ranking y los
 * gates (consentimiento → cuestionarios pendientes → sensores).
 *
 * Decisión de producto de mg (2026-09-07, doc 32 §4): "Inicio no tiene nada,
 * no tiene sentido que esté separado de Train". Por eso desaparecieron de
 * acá el panel de consentimiento (vive en `/consent`, para el camino de
 * cuestionarios en la web), el prompt de cuestionario pendiente (queda como
 * card en `/data`) y el redirect a `/data/profile` (queda como CTA en
 * `/data`): ninguno bloquea el camino al juego.
 *
 * Ruteo por rol antes de puentear (mismo orden que `postLoginRoute`):
 *   - staff de club sin perfil de jugador → `/club` (su panel).
 *   - superadmin sin perfil de jugador → `/admin`.
 *   - el resto (jugador, o staff/admin que ADEMÁS es jugador) → al vr-app.
 *
 * El jugador SIN CLUB no llega hasta acá: lo desvía `RequireClub` a
 * `/sin-club` antes de montar este componente (mg, 2026-09-09). Puentearlo
 * habría abierto el lobby del vr-app para que el 403 `club_required` lo
 * frenara recién al tocar "Comenzar".
 *
 * Jugador de club SIN PIN (entró con Google o contraseña, o el mail no le
 * llegó): antes del puente se le ofrece crearlo, porque es lo que va a tipear
 * en la tablet o el visor del club (mg, 2026-10-07). Es opcional: "Ahora no"
 * sigue al juego y no se vuelve a preguntar en esa pestaña.
 *
 * Se monta dentro de `AppShell` (footer + bottom bar globales, ver
 * CLAUDE.md), así el tab VR sigue a mano si el puente falla.
 */

const PIN_SKIP_KEY = 'vrs.pinPromptSkipped'

function pinPromptSkipped(userId: number): boolean {
  try {
    return sessionStorage.getItem(PIN_SKIP_KEY) === String(userId)
  } catch {
    return false
  }
}

function skipPinPrompt(userId: number) {
  try {
    sessionStorage.setItem(PIN_SKIP_KEY, String(userId))
  } catch {
    // Sin storage se vuelve a ofrecer en la próxima entrada: no es grave.
  }
}
export function PlayBridge() {
  const { user, refresh } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [pinSkipped, setPinSkipped] = useState(() =>
    user ? pinPromptSkipped(user.user_id) : false,
  )

  const isClubStaff = (user?.club_memberships?.length ?? 0) > 0
  const redirect = !user || user.player_club
    ? null
    : isClubStaff
      ? '/club'
      : hasAdminAreaAccess(user)
        ? '/admin'
        : null

  const open = useCallback(() => {
    setError(null)
    void openVrApp().catch((e: unknown) => setError(vrBridgeError(e)))
  }, [])

  const askPin = !redirect && !!user?.player_club && user.has_pin === false && !pinSkipped

  // El doble montaje de StrictMode en dev dispararía dos
  // `POST /auth/exchange-token`; el canje automático corre una sola vez y el
  // botón "Reintentar" queda como el único camino manual.
  const auto = useRef(false)
  useEffect(() => {
    if (redirect || askPin || auto.current) return
    auto.current = true
    open()
  }, [redirect, askPin, open])

  if (redirect) return <Navigate to={redirect} replace />

  if (askPin && user) {
    return (
      <div className="mx-auto max-w-md px-lg py-2xl">
        <div className="panel max-w-md p-xl">
          <h1 className="m-0">Creá tu PIN</h1>
          <p className="mt-sm text-sm text-text-muted">
            Con este PIN de 6 dígitos entrás desde el visor o la tablet de{' '}
            {user.player_club?.name ?? 'tu club'}, sin escribir tu contraseña.
          </p>
          <PinForm
            submitLabel="Guardar PIN y jugar"
            onSubmitPin={async (pin) => {
              await auth.pinCreate(pin)
              await refresh()
            }}
            secondary={
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  skipPinPrompt(user.user_id)
                  setPinSkipped(true)
                }}
              >
                Ahora no
              </button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-lg px-lg py-2xl text-center">
      <VerSportsLogo size="lg" />
      {error ? (
        <div className="flex w-full max-w-md flex-col gap-md">
          <div className="banner-err">{error}</div>
          <button type="button" className="btn-primary" onClick={open}>
            Reintentar
          </button>
        </div>
      ) : (
        <div className="font-mono text-xs uppercase tracking-[0.18em] text-text-muted">
          Abriendo el entrenamiento…
        </div>
      )}
    </div>
  )
}
